import { useEffect, useRef, useState, useCallback } from "react";
import { tokenStore } from "../api/client";

export type RealtimeEvent =
  | { event: "phone:update"; room: string; payload: PhoneUpdatePayload }
  | { event: "phone:log";    room: string; payload: PhoneLogPayload }
  | { event: "job:update";   room: string; payload: JobUpdatePayload }
  | { event: "run:update";   room: string; payload: RunUpdatePayload }
  | { event: "account:created"; room: string; payload: AccountCreatedPayload }
  | { event: "account:metrics"; room: string; payload: AccountMetricsPayload };

export interface PhoneUpdatePayload {
  phone_id: string;
  name?: string;
  status?: "online" | "offline" | "busy" | "error";
  computed_status?: "online" | "offline" | "busy" | "error" | "paused";
  last_heartbeat_at?: string;
  os_version?: string | null;
  model?: string | null;
  spoofed_model?: string | null;
}

export interface PhoneLogPayload {
  phone_id: string;
  run_id: string | null;
  level: "debug" | "info" | "warn" | "error";
  source: string;
  message: string;
  created_at: string;
}

export interface JobUpdatePayload {
  job_id: string;
  status?: string;
  name?: string;
  mode?: string;
  max_runs?: number | null;
  current_run_count?: number;
  ended_at?: string;
}

export interface RunUpdatePayload {
  run_id: string;
  job_id: string;
  phone_id: string;
  status: string;
  started_at?: string;
  ended_at?: string;
  duration_ms?: number;
  account_id?: string | null;
  failure_reason?: string | null;
}

export interface AccountCreatedPayload {
  account_id: string;
  phone_id: string | null;
  run_id: string | null;
  ig_username: string;
  created_at: string;
}

export interface AccountMetricsPayload {
  account_id: string;
  phone_id?: string;
  followers?: number | null;
  following?: number | null;
  posts_count?: number | null;
  sync_status?: "never" | "ok" | "stale" | "error" | "checkpoint" | "banned";
  last_synced_at?: string;
}

type EventHandler = (evt: RealtimeEvent) => void;

interface State {
  connected: boolean;
  reconnectAttempt: number;
}

const RECONNECT_BASE_MS = 1000;
const RECONNECT_MAX_MS = 30000;
const PING_INTERVAL_MS = 25000;

/**
 * Hook unique pour s'abonner au canal `/api/realtime` du hub.
 *
 * - Reconnexion exponentielle avec jitter (max 30s)
 * - Ping/pong client → serveur toutes les 25s pour garder la connexion vivante
 * - Subscription idempotente : si on change `rooms` à chaud, on send sub/unsub
 *
 * @param rooms     Liste des rooms à subscribe (ex: ["phones", "phone:abc"])
 * @param handler   Callback appelé pour chaque event reçu
 */
export function useRealtimeChannel(rooms: string[], handler: EventHandler): State {
  const [state, setState] = useState<State>({ connected: false, reconnectAttempt: 0 });
  const wsRef = useRef<WebSocket | null>(null);
  const handlerRef = useRef(handler);
  const reconnectTimerRef = useRef<number | null>(null);
  const pingTimerRef = useRef<number | null>(null);
  const subscribedRoomsRef = useRef<Set<string>>(new Set());
  const desiredRoomsRef = useRef<Set<string>>(new Set(rooms));
  const attemptRef = useRef(0);
  const shouldReconnectRef = useRef(true);

  // Garde la dernière version du handler sans re-init la WS
  useEffect(() => {
    handlerRef.current = handler;
  }, [handler]);

  // Met à jour la liste des rooms désirées
  useEffect(() => {
    desiredRoomsRef.current = new Set(rooms);
    syncSubscriptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rooms.join("|")]);

  const send = useCallback((obj: unknown) => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return false;
    try {
      ws.send(JSON.stringify(obj));
      return true;
    } catch {
      return false;
    }
  }, []);

  const syncSubscriptions = useCallback(() => {
    const desired = desiredRoomsRef.current;
    const current = subscribedRoomsRef.current;
    // À unsubscribe : présent dans current mais pas dans desired
    current.forEach((r) => {
      if (!desired.has(r)) {
        if (send({ type: "unsubscribe", room: r })) current.delete(r);
      }
    });
    // À subscribe : présent dans desired mais pas dans current
    desired.forEach((r) => {
      if (!current.has(r)) {
        if (send({ type: "subscribe", room: r })) current.add(r);
      }
    });
  }, [send]);

  const cleanupTimers = useCallback(() => {
    if (reconnectTimerRef.current) {
      window.clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
    if (pingTimerRef.current) {
      window.clearInterval(pingTimerRef.current);
      pingTimerRef.current = null;
    }
  }, []);

  const connect = useCallback(() => {
    cleanupTimers();
    const token = tokenStore.get();
    if (!token) return;

    const proto = window.location.protocol === "https:" ? "wss" : "ws";
    const url = `${proto}://${window.location.host}/api/realtime?token=${encodeURIComponent(token)}`;

    let ws: WebSocket;
    try {
      ws = new WebSocket(url);
    } catch {
      scheduleReconnect();
      return;
    }
    wsRef.current = ws;

    ws.addEventListener("open", () => {
      attemptRef.current = 0;
      setState({ connected: true, reconnectAttempt: 0 });
      subscribedRoomsRef.current.clear();
      syncSubscriptions();
      pingTimerRef.current = window.setInterval(() => {
        send({ type: "ping" });
      }, PING_INTERVAL_MS);
    });

    ws.addEventListener("message", (e) => {
      let parsed: { type?: string } & Record<string, unknown>;
      try {
        parsed = JSON.parse(e.data as string);
      } catch {
        return;
      }
      if (parsed.type === "event") {
        handlerRef.current(parsed as unknown as RealtimeEvent);
      }
    });

    ws.addEventListener("close", () => {
      wsRef.current = null;
      cleanupTimers();
      subscribedRoomsRef.current.clear();
      setState((s) => ({ connected: false, reconnectAttempt: s.reconnectAttempt }));
      if (shouldReconnectRef.current) scheduleReconnect();
    });

    ws.addEventListener("error", () => {
      try { ws.close(); } catch (_) {}
    });
  }, [cleanupTimers, send, syncSubscriptions]);

  const scheduleReconnect = useCallback(() => {
    const next = attemptRef.current + 1;
    attemptRef.current = next;
    setState((s) => ({ ...s, reconnectAttempt: next }));
    const delay = Math.min(RECONNECT_BASE_MS * 2 ** (next - 1), RECONNECT_MAX_MS);
    const jittered = delay * (0.7 + Math.random() * 0.6);
    reconnectTimerRef.current = window.setTimeout(() => {
      if (shouldReconnectRef.current) connect();
    }, jittered);
  }, [connect]);

  useEffect(() => {
    shouldReconnectRef.current = true;
    connect();
    return () => {
      shouldReconnectRef.current = false;
      cleanupTimers();
      const ws = wsRef.current;
      if (ws) {
        try { ws.close(); } catch (_) {}
        wsRef.current = null;
      }
    };
    // Connecte une seule fois au mount, la sync rooms est faite à part
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return state;
}
